import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, ExternalLink, CheckCircle, ArrowUpRight, Palette, FileText, Code, Calendar, Clock } from 'lucide-react';

const SingleWidgetManager = ({ widget }) => {
  const navigate = useNavigate();

  // Handle case where no widget exists yet
  if (!widget) {
    return (
      <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] overflow-hidden">
        <div className="p-6">
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-[14px] bg-gray-50 dark:bg-white/[0.04] flex items-center justify-center mx-auto mb-6">
              <Globe className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Create Your Widget</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-sm mx-auto">
              Set up your AI-powered chat widget to start engaging with customers instantly.
            </p>
            <button
              onClick={() => navigate('/dashboard/appearance')}
              className="inline-flex items-center space-x-2 bg-black dark:bg-white text-white dark:text-black px-6 py-3 rounded-full font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-all duration-200"
            >
              <span>Get Started</span>
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-100 dark:border-white/[0.06] p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-[14px] bg-gray-50 dark:bg-white/[0.04] flex items-center justify-center">
              <Globe className="w-6 h-6 text-gray-500 dark:text-gray-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{widget.name || 'My Chat Widget'}</h3>
              <div className="flex items-center space-x-2 mt-1">
                {widget.url ? (
                  <a
                    href={widget.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 group hover:opacity-80 transition-opacity"
                  >
                    <span className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200 transition-colors">
                      {widget.url}
                    </span>
                    <ExternalLink className="w-3 h-3 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
                  </a>
                ) : (
                  <span className="text-sm text-orange-600 dark:text-orange-400 font-medium">Not set</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
              widget.status === 'active'
                ? 'bg-green-500/[0.08] text-green-600 dark:text-green-400'
                : 'bg-orange-500/[0.08] text-orange-600 dark:text-orange-400'
            }`}>
              {widget.status === 'active' ? 'Live' : 'Paused'}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Quick Info Grid */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <div className="bg-gray-50 dark:bg-white/[0.03] rounded-[12px] p-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500 mb-1">
              <Palette className="w-3.5 h-3.5" />
              <span>Theme</span>
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white capitalize">
              {widget.appearance?.theme || 'Light'} Mode
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-white/[0.03] rounded-[12px] p-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500 mb-1">
              <Globe className="w-3.5 h-3.5" />
              <span>Position</span>
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white capitalize">
              {widget.appearance?.position?.replace(/-/g, ' ') || 'Bottom Right'}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-white/[0.03] rounded-[12px] p-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500 mb-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>Created</span>
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {new Date(widget.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-white/[0.03] rounded-[12px] p-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500 mb-1">
              <Clock className="w-3.5 h-3.5" />
              <span>Updated</span>
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {new Date(widget.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
        </div>

        {/* Clickable Action Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/dashboard/sources')}
            className="bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-[16px] p-4 hover:bg-gray-100 dark:hover:bg-white/[0.05] transition-all duration-200 text-left group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-[10px] bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center">
                  <FileText className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">Sources</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Manage knowledge base</div>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
            </div>
          </button>

          <button
            onClick={() => navigate('/dashboard/integration')}
            className="bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-[16px] p-4 hover:bg-gray-100 dark:hover:bg-white/[0.05] transition-all duration-200 text-left group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-[10px] bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center">
                  <Code className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">Integration</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Get embed code</div>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
            </div>
          </button>
        </div>

        {/* Status Card */}
        <div className="bg-green-500/[0.05] border border-green-500/20 rounded-[16px] p-4">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            <div>
              <h4 className="font-medium text-green-900 dark:text-green-100">Widget Active</h4>
              <p className="text-sm text-green-700 dark:text-green-300">Your chat widget is live and ready to engage visitors</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/dashboard/appearance')}
            className="flex-1 inline-flex items-center justify-center space-x-2 px-4 py-3 bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-100 rounded-full font-medium transition-all duration-200"
          >
            <Palette className="w-4 h-4" />
            <span>Customize</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SingleWidgetManager;
